import { useTranslation } from 'react-i18next';
// GlobalNotifications.jsx - WEBSOCKET VERSION: Usa useWebSocketEvent en lugar de useSystemNotifications
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Slide,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
// 🔄 WEBSOCKET: Cambiar useSystemNotifications por useWebSocketEvent
import { useWebSocketEvent, useWebSocket } from '../../hooks/useWebSocket';

const toDisplayMessage = (message) => {
  if (message === null || message === undefined) return 'Ocurrió un error inesperado';
  if (typeof message === 'string') return message;
  if (typeof message === 'number' || typeof message === 'boolean') return String(message);

  if (Array.isArray(message)) {
    return message.map((item) => toDisplayMessage(item)).filter(Boolean).join(' | ');
  }

  if (typeof message === 'object') {
    if (typeof message.detail === 'string') return message.detail;
    if (Array.isArray(message.detail)) return toDisplayMessage(message.detail);
    if (typeof message.msg === 'string') return message.msg;
    if (typeof message.message === 'string') return message.message;

    try {
      return JSON.stringify(message);
    } catch {
      return 'Ocurrió un error inesperado';
    }
  }

  return String(message);
};

// Notification context for global notifications
const NotificationContext = React.createContext();

export const useNotification = () => {
  const { t } = useTranslation();
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Notification Provider
export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info',
      autoHideDuration: 6000,
      ...notification,
      message: toDisplayMessage(notification?.message),
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    if (newNotification.autoHideDuration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.autoHideDuration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({ type: 'success', message, ...options });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    return addNotification({ type: 'error', message, autoHideDuration: 8000, ...options });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({ type: 'warning', message, ...options });
  }, [addNotification]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({ type: 'info', message, ...options });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Slide up transition
function SlideTransition(props) {
  const { t } = useTranslation();
  return <Slide {...props} direction="up" />;
}

// Main Notifications Component
export const GlobalNotifications = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { notifications, removeNotification, addNotification } = useNotification();

  // 🔄 WEBSOCKET: Track displayed notification IDs to avoid duplicates
  const displayedNotificationsRef = useRef(new Set());

  // 🔄 WEBSOCKET: Get connection status
  const { isConnected } = useWebSocket();

  // 🔄 WEBSOCKET: Handler for system notifications from WebSocket
  const handleSystemNotification = useCallback((data) => {
    // Skip alarm-related notifications as they are handled by navbar
    if (data.type === 'alarm' || data.category === 'alarm') {
      return;
    }

    // Generate unique ID for deduplication
    const notifId = data.id || `${data.type}-${data.message}-${Date.now()}`;

    // Skip if already displayed
    if (displayedNotificationsRef.current.has(notifId)) {
      return;
    }

    // Mark as displayed
    displayedNotificationsRef.current.add(notifId);

    // Add notification
    addNotification({
      type: data.type || data.severity || 'info',
      message: data.message,
      title: data.title,
      autoHideDuration: data.persistent ? 0 : (data.duration || 6000),
    });

    // Clean up old IDs to prevent memory leak (keep last 100)
    if (displayedNotificationsRef.current.size > 100) {
      const idsArray = Array.from(displayedNotificationsRef.current);
      displayedNotificationsRef.current = new Set(idsArray.slice(-50));
    }
  }, [addNotification]);

  // 🔄 WEBSOCKET: Subscribe to system notification events
  useWebSocketEvent('system_notification', handleSystemNotification);
  useWebSocketEvent('notification', handleSystemNotification);
  useWebSocketEvent('broadcast', handleSystemNotification);

  // 🔄 WEBSOCKET: Also handle connection status changes
  useWebSocketEvent('connection_established', useCallback((data) => {
    // Optional: Show connection established notification
    // addNotification({
    //   type: 'success',
    //   message: 'Conexión en tiempo real establecida',
    //   autoHideDuration: 3000,
    // });
  }, []));

  useWebSocketEvent('connection_error', useCallback((data) => {
    addNotification({
      type: 'warning',
      message: t('hardcoded.components_common_globalnotifications.conexion_en_tiempo_real_perdida'),
      autoHideDuration: 5000,
    });
  }, [addNotification]));

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  };

  const getSeverity = (type) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1,
        maxWidth: 400,
        width: '100%',
        pointerEvents: 'none',
        [theme.breakpoints.down('sm')]: {
          left: 16,
          right: 16,
          maxWidth: 'none',
          bottom: 90, // Account for mobile UI elements
        },
      }}
    >
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          TransitionComponent={SlideTransition}
          sx={{
            position: 'relative',
            transform: 'none !important',
            left: 'auto !important',
            right: 'auto !important',
            bottom: 'auto !important',
            top: 'auto !important',
            pointerEvents: 'auto',
            width: '100%',
            marginTop: index > 0 ? 1 : 0,
          }}
        >
          <Alert
            severity={getSeverity(notification.type)}
            icon={getIcon(notification.type)}
            variant="filled"
            sx={{
              width: '100%',
              alignItems: 'flex-start',
              '& .MuiAlert-icon': {
                marginTop: '2px',
              },
              boxShadow: theme.shadows[8],
            }}
            action={
              <IconButton
                size="small"
                aria-label={t('hardcoded.components_common_globalnotifications.close')}
                color="inherit"
                onClick={() => removeNotification(notification.id)}
                sx={{
                  padding: 0.5,
                  marginTop: -0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {notification.title && (
              <AlertTitle sx={{ marginBottom: 0.5, fontWeight: 600 }}>
                {notification.title}
              </AlertTitle>
            )}
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};

// Enhanced Global Notifications with Provider
export const GlobalNotificationsWithProvider = () => {
  const { t } = useTranslation();
  return (
    <NotificationProvider>
      <GlobalNotifications />
    </NotificationProvider>
  );
};

export default GlobalNotifications;